import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Breadcrumb from "@/components/Breadcrumb";
import EmptyState from "@/components/EmptyState";
import { ActionCluster, type Action } from "@/components/MenuButton";
import ObjectHeading from "@/components/ObjectHeading";
import PageHeading from "@/components/PageHeading";
import { ObjectHeaderSkeleton, TableSkeleton, TabsSkeleton } from "@/components/Skeleton";
import Tabs from "@/components/Tabs";
import Tag from "@/components/Tag";
import { useCanManageCommitteesBool } from "@/hooks/useCapabilities";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useParams as useUrlParams } from "@/hooks/useSearchParams";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import type { CommitteeCatalogScope } from "../committeeScope";
import {
  COMMITTEE_FROM_ALL_PARAM,
  COMMITTEE_FROM_ALL_VALUE,
  COMMITTEE_PARTICIPATION_PARAM,
  nationalCommitteeCatalogPath,
  SECTION_OPERATIONS_COMMITTEES_ROUTE
} from "../constants";
import { UPDATE_BACKOFFICE_COMMITTEE_MUTATION } from "../mutations";
import { BACKOFFICE_COMMITTEE_QUERY, BACKOFFICE_COMMITTEES_QUERY } from "../queries";
import type {
  BackofficeCommitteeQueryResponse,
  BackofficeCommitteeQueryVariables,
  CommitteeParticipationFilter,
  CommitteeRow
} from "../types";
import AddCommitteeParticipantsSheet from "./AddCommitteeParticipantsSheet";
import CommitteeDialog, { type CommitteeDialogSaveInput } from "./CommitteeDialog";
import CommitteeParticipantsTable from "./CommitteeParticipantsTable";
import CommitteeStatusChangeDialog, { committeeStatusChangeMode } from "./CommitteeStatusChangeDialog";

const PARTICIPATION_TABS: { label: string; value: CommitteeParticipationFilter }[] = [
  { label: "All Participations", value: "all" },
  { label: "Active Participations", value: "active" },
  { label: "Inactive Participations", value: "inactive" }
];

function parseParticipation(value: unknown): CommitteeParticipationFilter {
  if (value === "active" || value === "inactive") return value;
  return "all";
}

type CommitteeDetailPageContentProps = {
  committeeId: string;
  committeeScope?: CommitteeCatalogScope;
  sectionName?: string;
  canManageCommittees?: boolean;
  showCatalogBreadcrumb?: boolean;
  expectedSectionId?: string;
  nestedHeading?: boolean;
};

export default function CommitteeDetailPageContent({
  committeeId,
  committeeScope = "national",
  sectionName,
  canManageCommittees: canManageCommitteesProp,
  showCatalogBreadcrumb = false,
  expectedSectionId,
  nestedHeading = false
}: CommitteeDetailPageContentProps) {
  const canManageFromPermission = useCanManageCommitteesBool();
  const canManageCommittees = canManageCommitteesProp ?? canManageFromPermission;
  const { isCompactLayout } = useIsCompactLayout();
  const { params, addSearchParam, deleteSearchParams } = useUrlParams();
  const participation = parseParticipation(params[COMMITTEE_PARTICIPATION_PARAM]);
  const fromAllCatalog = params[COMMITTEE_FROM_ALL_PARAM] === COMMITTEE_FROM_ALL_VALUE;
  const [committeeDialogOpen, setCommitteeDialogOpen] = useState(false);
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);
  const [addPeopleOpen, setAddPeopleOpen] = useState(false);

  const { data, loading, error } = useQuery<BackofficeCommitteeQueryResponse, BackofficeCommitteeQueryVariables>(
    BACKOFFICE_COMMITTEE_QUERY,
    {
      variables: { id: committeeId },
      skip: !committeeId
    }
  );

  const [updateCommittee, { loading: updatingCommittee }] = useMutation(UPDATE_BACKOFFICE_COMMITTEE_MUTATION, {
    refetchQueries: [BACKOFFICE_COMMITTEE_QUERY, BACKOFFICE_COMMITTEES_QUERY]
  });

  const loadedCommittee = data?.backofficeCommittee ?? null;
  const committee =
    loadedCommittee && (!expectedSectionId || loadedCommittee.section.id === expectedSectionId)
      ? loadedCommittee
      : null;
  const catalogBackPath = nationalCommitteeCatalogPath(fromAllCatalog ? "all" : "active");

  const handleCommitteeDialogSave = useCallback(
    async (input: CommitteeDialogSaveInput) => {
      if (input.mode !== "edit") return;

      try {
        const { data: mutationData } = await updateCommittee({
          variables: {
            input: {
              id: input.committeeId,
              name: input.name,
              status: input.status,
              core: input.core
            }
          }
        });

        const result = mutationData?.updateBackofficeCommittee;
        if (!result?.success) {
          const apiMessage = result?.message;
          if (apiMessage) {
            showErrorSnackbar("Committee not updated", { subtitle: apiMessage });
          } else {
            showErrorSnackbar("Committee not updated", { subtitle: "Please try again later." });
          }
          return;
        }

        showSnackbar("Committee updated");
        setCommitteeDialogOpen(false);
      } catch {
        showErrorSnackbar("Committee not updated", { subtitle: "Please try again later." });
      }
    },
    [updateCommittee]
  );

  const handleStatusChangeConfirm = useCallback(async () => {
    if (!committee) return;

    const nextStatus = committee.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    try {
      const { data: mutationData } = await updateCommittee({
        variables: {
          input: {
            id: committee.id,
            status: nextStatus
          }
        }
      });

      const result = mutationData?.updateBackofficeCommittee;
      if (!result?.success) {
        const apiMessage = result?.message;
        if (apiMessage) {
          showErrorSnackbar("Committee status not updated", { subtitle: apiMessage });
        } else {
          showErrorSnackbar("Committee status not updated", { subtitle: "Please try again later." });
        }
        return;
      }

      showSnackbar(nextStatus === "INACTIVE" ? "Committee deactivated" : "Committee activated");
      setStatusChangeOpen(false);
    } catch {
      showErrorSnackbar("Committee status not updated", { subtitle: "Please try again later." });
    }
  }, [committee, updateCommittee]);

  const headerActions = useMemo((): Action[] => {
    if (!canManageCommittees || !committee) return [];

    return [
      {
        text: "Add People",
        onClick: () => setAddPeopleOpen(true),
        testId: "add-committee-participants-button"
      },
      {
        text: "Edit Committee",
        onClick: () => setCommitteeDialogOpen(true),
        testId: "edit-committee-button",
        section: "Manage"
      },
      {
        text: committee.status === "ACTIVE" ? "Deactivate Committee" : "Activate Committee",
        onClick: () => setStatusChangeOpen(true),
        testId: "committee-status-change-action",
        section: "Manage"
      }
    ];
  }, [canManageCommittees, committee]);

  const nestedCatalogBreadcrumb =
    nestedHeading && expectedSectionId ? (
      <Breadcrumb
        title="Committees"
        compactTitle={committee?.name ?? "Committee"}
        backLink={{
          to: SECTION_OPERATIONS_COMMITTEES_ROUTE,
          params: { sectionId: expectedSectionId }
        }}
      />
    ) : null;

  if (loading && !loadedCommittee) {
    return (
      <div data-testid="committee-detail-skeleton">
        {nestedCatalogBreadcrumb}
        <ObjectHeaderSkeleton />
        <TabsSkeleton tabCount={3} />
        <TableSkeleton columns={5} rows={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        {nestedCatalogBreadcrumb}
        <EmptyState title="Unable to Load Committee" caption={error.message} iconName="error" />
      </div>
    );
  }

  if (!committee) {
    return (
      <div>
        {nestedCatalogBreadcrumb}
        <EmptyState
          title="Committee Not Found"
          caption="This committee may have been removed, or the link may be out of date."
          iconName="groups"
        />
      </div>
    );
  }

  const committeeRow: CommitteeRow = committee;
  const statusTag = (
    <Tag tagType={committee.status === "ACTIVE" ? "success" : "surface"} tagStyle="strong" size="small">
      {committee.status === "ACTIVE" ? "Active" : "Inactive"}
    </Tag>
  );

  return (
    <div className="w-full" data-testid="committee-detail-page">
      {nestedHeading ? (
        <>
          {nestedCatalogBreadcrumb}
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <PageHeading title={committee.name} compact={!isCompactLayout} />
              <div className="flex flex-wrap items-center gap-3">
                {statusTag}
                <p className="text-body-medium text-sys-on-surface-variant">Core: {committee.core ? "Yes" : "No"}</p>
              </div>
            </div>
            {headerActions.length > 0 ? (
              <div
                className={
                  isCompactLayout
                    ? "flex shrink-0 flex-wrap items-center gap-2"
                    : "flex shrink-0 flex-wrap items-center gap-2 md:pt-4"
                }
              >
                <ActionCluster actions={headerActions} />
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <>
          {showCatalogBreadcrumb && (
            <Breadcrumb
              title="Committee Management"
              compactTitle={committee.name}
              backLink={{ to: catalogBackPath }}
              compact
            />
          )}
          <ObjectHeading
            title={committee.name}
            iconName="groups"
            isDetailsView
            tag={statusTag}
            metaData={[{ text: committee.core ? "Yes" : "No", label: "Core" }]}
            actions={headerActions}
          />
        </>
      )}
      <Tabs
        tabItems={PARTICIPATION_TABS.map((tab) => ({
          label: tab.label,
          active: tab.value === participation,
          onClick: () => {
            deleteSearchParams(["currentPage", "after", "before"]);
            if (tab.value === "all") {
              deleteSearchParams(COMMITTEE_PARTICIPATION_PARAM);
              return;
            }
            addSearchParam(COMMITTEE_PARTICIPATION_PARAM, tab.value);
          }
        }))}
      />
      <CommitteeParticipantsTable
        key={`${committee.id}-${participation}`}
        committeeId={committee.id}
        committeeName={committee.name}
        participation={participation}
        canManageCommittees={canManageCommittees}
      />
      <CommitteeDialog
        open={committeeDialogOpen}
        mode="edit"
        initialCommittee={committeeRow}
        saving={updatingCommittee}
        onClose={() => setCommitteeDialogOpen(false)}
        onSave={handleCommitteeDialogSave}
      />
      <CommitteeStatusChangeDialog
        open={statusChangeOpen}
        committee={committeeRow}
        mode={committeeStatusChangeMode(committee.status)}
        saving={updatingCommittee}
        onClose={() => setStatusChangeOpen(false)}
        onConfirm={handleStatusChangeConfirm}
      />
      <AddCommitteeParticipantsSheet
        open={addPeopleOpen}
        committeeId={committee.id}
        committeeName={committee.name}
        committeeScope={committeeScope}
        sectionName={sectionName}
        onClose={() => setAddPeopleOpen(false)}
      />
    </div>
  );
}
